/**
 * Truncate a numeric input string to at most `maxLength` numeric digits.
 *
 * Non-digit characters (a leading sign, a decimal point) are preserved and do
 * not count toward the limit. Returns the value unchanged when `maxLength` is
 * not a positive number.
 *
 * @example
 * truncateNumber('20260', 4) => '2026'
 * truncateNumber('-12.345', 4) => '-12.34'
 * truncateNumber('123456') => '123456'
 */
export function truncateNumber(value, maxLength) {
    if (!value || !maxLength || maxLength <= 0)
        return value;
    let digits = 0;
    return Array.from(value).reduce((result, char) => {
        const isDigit = char >= '0' && char <= '9';
        if (isDigit && digits >= maxLength)
            return result;
        if (isDigit)
            digits += 1;
        return result + char;
    }, '');
}
/**
 * Digit cap for a fully bounded number field.
 *
 * A cap is only meaningful when the value's magnitude is bounded on both sides,
 * so this returns `undefined` unless BOTH `min` and `max` are set — an open-ended
 * range (no `max`, or no `min` while negatives are allowed) must not be capped.
 * When both are set, the cap is the larger digit count of the two bounds by
 * magnitude (the sign is not counted, since `truncateNumber` ignores it) — so
 * `[-5000, 100]` yields 4, not 3.
 *
 * @example
 * getNumberMaxLength(1900, 2026) => 4
 * getNumberMaxLength(-5000, 100) => 4
 * getNumberMaxLength(undefined, 500) => undefined // min open => could be -100000
 * getNumberMaxLength(0, undefined) => undefined   // max open => unbounded above
 */
export function getNumberMaxLength(min, max) {
    if (typeof min !== 'number' || typeof max !== 'number')
        return undefined;
    const digitCount = (value) => Math.abs(value).toString().replace(/\D/g, '').length;
    return Math.max(digitCount(min), digitCount(max));
}
